import React, {useState} from 'react'
import {Row, Col, ButtonLink, RegularBlock} from '@modifi/ui'
import {map} from 'ramda'
import styled from 'styled-components'
import {CountryName} from '@modifi/plugin-countries'
import {PublicRegisteredAddressView} from '@modifi/onboarding-api-clients'
import {semiDarkGray} from '@modifi/colors'
import {useTranslation} from '@modifi/plugin-i18n'

import {DisclosureWidget} from '../../../ui/disclosure-widget'

const StyledButtonLink = styled(ButtonLink)`
  pointer-events: auto;
  cursor: pointer;
`

type AddressType = {
  streetName?: string | null
  streetNumber?: string | null
  city?: string | null
  state?: string | null
  zipCode?: string | null
  countryCode?: string | null
}

const addressKeyList: (keyof AddressType)[] = [
  'streetName',
  'streetNumber',
  'city',
  'state',
  'zipCode',
  'countryCode',
]
const getCombinedAddress = (data: AddressType): string =>
  addressKeyList.map(key => data?.[key]).join(' ')

type PersonAdditionalInfo = {
  dateOfBirth?: string | null
  emailAddress?: string | null
  nationality?: string | null
  panNumber?: string | null
  address?: AddressType | null
}

type PersonDetailsProps = {
  fullName: string
  additionalInfo: PersonAdditionalInfo
}

const PersonDetails: React.VFC<PersonDetailsProps> = ({fullName, additionalInfo}) => {
  const [showDetails, setShowDetails] = useState(false)
  const {t} = useTranslation('charon')
  const handleDisclosureWidgetClick = () => setShowDetails(!showDetails)
  const combinedAddress = getCombinedAddress(additionalInfo?.address || {})
  return (
    <>
      <Row>
        <Col col={8}>{fullName}</Col>
        <Col col={2}>
          <StyledButtonLink
            data-target="signatory-details"
            type="button"
            onClick={handleDisclosureWidgetClick}
            transparent
          >
            <DisclosureWidget expandItem={showDetails} />
          </StyledButtonLink>
        </Col>
      </Row>
      {showDetails && (
        <Row mb={16}>
          <Col>
            {additionalInfo?.dateOfBirth && (
              <RegularBlock color={semiDarkGray} mb={8}>
                {t('pre-fill-details-page.person-details.date-of-birth.label')}
                {additionalInfo.dateOfBirth}
              </RegularBlock>
            )}
            {additionalInfo?.nationality && (
              <RegularBlock color={semiDarkGray} mb={8}>
                {t('pre-fill-details-page.person-details.nationality.label')}{' '}
                <CountryName>{additionalInfo.nationality}</CountryName>
              </RegularBlock>
            )}
            {combinedAddress && (
              <RegularBlock color={semiDarkGray} mb={8}>
                {t('pre-fill-details-page.person-details.address.label')}: {combinedAddress}
              </RegularBlock>
            )}
            {additionalInfo?.emailAddress && (
              <RegularBlock color={semiDarkGray} mb={8}>
                {additionalInfo.emailAddress}
              </RegularBlock>
            )}
            {additionalInfo?.panNumber && (
              <RegularBlock color={semiDarkGray} mb={8}>
                {t('pre-fill-details-page.person-details.pan-number.label')}{' '}
                {additionalInfo.panNumber}
              </RegularBlock>
            )}
          </Col>
        </Row>
      )}
    </>
  )
}

export const mapPersonsToOptions = map<
  {
    firstName?: string | null
    lastName?: string | null
    providerPersonId: string
    address?: PublicRegisteredAddressView
    emailAddress?: string
    dateOfBirth?: string
    nationality?: string
    panNumber?: string
  },
  {
    label: React.ReactNode
    value: string
  }
>(
  ({
    firstName,
    lastName,
    providerPersonId,
    address,
    emailAddress,
    dateOfBirth,
    panNumber,
    nationality,
  }) => {
    const additionalInfo = {address, emailAddress, dateOfBirth, panNumber, nationality}
    return {
      label: (
        <PersonDetails
          fullName={`${firstName || ''} ${lastName || ''}`}
          additionalInfo={additionalInfo}
        />
      ),
      value: providerPersonId,
    }
  }
)
